import type { ReactElement } from "react"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import Header from "@gatsby-plugin-generic-page/components/Header"
import ResponsiveImage from "@gatsby-plugin-generic-page/components/ResponsiveImage"
import Trustpilot from "@gatsby-plugin-generic-page/components/Trustpilot"
import type { InfoCardsFlatData } from "@gatsby-plugin-generic-page/fragments/infoCards"
import { getBgColorClass } from "@gatsby-plugin-generic-page/fragments/udsBackgroundColor"
import { Box, Divider, Text } from "@ionos-web-design-system/react"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"

const GRID_COLS_CLASS: { [key: number]: string } = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
}

const imageClass =
  "w-16 h-16 shrink-0 [&_img]:w-full [&_img]:h-full [&_img]:object-contain"

export default function InfoCardsSection({
  boxes,
  header,
  showTrustpilot,
  sectionSettings,
}: InfoCardsFlatData): ReactElement<InfoCardsFlatData> {
  const gridColsClass = GRID_COLS_CLASS[boxes.length] ?? "md:grid-cols-4"
  const containerSettings = sectionSettings?.containerSettings
  const hasBackgroundColor = !isEmpty(containerSettings?.backgroundColor)

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer
        {...containerSettings}
        className={cn(
          "xl:!max-w-[76rem] !w-auto !mx-3 xl:!mx-auto",
          // No content-configured color yet: paint the card white the same way
          // production always has — a plain class, not the `backgroundColor`
          // prop, so it doesn't also switch BaseContainer into its "has
          // background" 48px padding preset (see ONESTORE-261 follow-up).
          !hasBackgroundColor && "bg-surface-base"
        )}
      >
        {!isEmpty(header) ? (
          <div className="mx-1 mb-2 mt-4 md:m-6 md:mb-0">
            <Header {...header} />
          </div>
        ) : null}

        <div
          className={cn(
            "flex flex-col md:grid lg:p-3",
            !isEmpty(header) && "lg:pt-0",
            gridColsClass
          )}
        >
          {boxes.map((box, index) => {
            const bgColorClass = getBgColorClass(box.backgroundColor)

            return (
              <div key={`${box.title}-${index}`}>
                {/* Mobile: horizontal row — image left, title right */}
                <div
                  className={cn(
                    "flex items-center gap-4 py-3 md:hidden",
                    bgColorClass
                  )}
                >
                  <div className={imageClass}>
                    <ResponsiveImage images={box.responsiveImages} />
                  </div>

                  <Text variant="headingXl" weight="black" color="base" asChild>
                    <h3>{box.title}</h3>
                  </Text>
                </div>

                {/* Desktop: Box — image top, title, description */}
                <div className="hidden md:block">
                  <Box
                    className={bgColorClass}
                    visual={{
                      icon: true,
                      iconContent: (
                        <div className={imageClass}>
                          <ResponsiveImage images={box.responsiveImages} />
                        </div>
                      ),
                    }}
                  >
                    <Text
                      variant="headingXl"
                      weight="black"
                      color="base"
                      className="pt-3"
                    >
                      {box.title}
                    </Text>

                    <Text className="pt-2" color="base">
                      {box.description}
                    </Text>
                  </Box>
                </div>

                {index < boxes.length - 1 ? (
                  <div className="md:hidden">
                    <Divider orientation="horizontal" />
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>

        {showTrustpilot ? (
          <div className="flex flex-col gap-4 md:gap-8">
            <div className="-mx-3 md:px-8">
              <Divider orientation="horizontal" />
            </div>

            <div className="px-8 pb-4 md:pb-8">
              <Trustpilot />
            </div>
          </div>
        ) : null}
      </BaseContainer>
    </BaseSection>
  )
}
